import React, { useEffect, useState } from "react";
import Header, { TopBar } from "@/components/Header";
import Footer from "@/components/Footer";
import FlightSearchWidget from "@/components/FlightSearchWidget";
import { PromoAndTicker, CouponSection, DealsOffers } from "@/components/home/TickerOffers";
import {
  WhyChooseUs,
  PopularDestinations,
  PopularAirlines,
} from "@/components/home/WhyDestinations";
import { TopAirlineDeals, TopRoutes } from "@/components/home/DealsRoutes";
import {
  Blogs,
  Testimonials,
  FaqSection,
  AboutStrip,
} from "@/components/home/BlogFaq";
import { Plane, ChevronLeft, ChevronRight, ShieldCheck, Headphones, BadgeIndianRupee, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Reveal, stagger, rise } from "@/components/home/Reveal";
import { apiListBanners } from "@/api";

const BannerCarousel = () => {
  const [banners, setBanners] = useState([]);
  const [idx, setIdx] = useState(0);
  useEffect(() => { apiListBanners().then((d) => setBanners(d.banners || [])).catch(() => {}); }, []);
  useEffect(() => {
    if (banners.length < 2) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % banners.length), 5000);
    return () => clearInterval(t);
  }, [banners.length]);
  if (banners.length === 0) return null;
  const b = banners[idx];
  const inner = (
    <div className="relative h-full w-full">
      <img src={b.image} alt={b.title} className="h-full w-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/25 to-transparent" />
      <div className="absolute inset-y-0 left-0 flex max-w-lg flex-col justify-center px-6 md:px-12">
        <h3 className="font-[Poppins] text-2xl font-extrabold text-white drop-shadow md:text-4xl">{b.title}</h3>
        {b.subtitle && <p className="mt-2 text-sm text-white/90 md:text-base">{b.subtitle}</p>}
        {b.cta && <span className="mt-4 inline-flex w-fit items-center gap-2 rounded-full bg-[#F5B32B] px-5 py-2.5 text-sm font-extrabold text-[#1B1F5B]">{b.cta} <ChevronRight className="h-4 w-4" /></span>}
      </div>
    </div>
  );
  return (
    <section className="mx-auto max-w-7xl px-4 py-6" data-testid="home-banners">
      <div className="relative h-52 overflow-hidden rounded-3xl shadow-lg md:h-72">
        {b.link ? (b.link.startsWith("http") ? <a href={b.link} target="_blank" rel="noreferrer" className="block h-full">{inner}</a> : <Link to={b.link} className="block h-full">{inner}</Link>) : inner}
        {banners.length > 1 && (
          <>
            <button onClick={() => setIdx((i) => (i - 1 + banners.length) % banners.length)} className="absolute left-3 top-1/2 -translate-y-1/2 rounded-full bg-white/80 p-2 text-[#1B1F5B] shadow hover:bg-white"><ChevronLeft className="h-4 w-4" /></button>
            <button onClick={() => setIdx((i) => (i + 1) % banners.length)} className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full bg-white/80 p-2 text-[#1B1F5B] shadow hover:bg-white"><ChevronRight className="h-4 w-4" /></button>
            <div className="absolute bottom-3 left-1/2 flex -translate-x-1/2 gap-1.5">
              {banners.map((_, i) => <span key={i} onClick={() => setIdx(i)} className={`h-2 cursor-pointer rounded-full transition-all ${i === idx ? "w-6 bg-white" : "w-2 bg-white/50"}`} />)}
            </div>
          </>
        )}
      </div>
    </section>
  );
};

const HERO_WORDS = ["Flights,", "Hotels,", "Buses", "&", "Holiday", "Packages"];
const TRUST = [
  { icon: ShieldCheck, label: "Secure Payments", sub: "100% protected" },
  { icon: BadgeIndianRupee, label: "Best Fares", sub: "Live from airlines" },
  { icon: Headphones, label: "24x7 Support", sub: "Real humans" },
];

const Home = () => {
  return (
    <div className="min-h-screen bg-[#F5F7FB]">
      <TopBar />
      <Header />

      {/* Hero with search widget */}
      <section className="relative overflow-hidden pb-16 pt-10" data-testid="home-hero">
        {/* Layered premium background: gradient mesh + moving orbs + grain */}
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-[#0A0F2E] via-[#141A47] to-[#1B1F5B]" />
        <motion.div
          className="pointer-events-none absolute -left-32 -top-24 h-[30rem] w-[30rem] rounded-full bg-[#0E7C6B]/30 blur-[110px]"
          animate={{ x: [0, 40, 0], y: [0, 30, 0] }}
          transition={{ duration: 16, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div
          className="pointer-events-none absolute -right-24 top-10 h-[26rem] w-[26rem] rounded-full bg-[#F5B32B]/25 blur-[110px]"
          animate={{ x: [0, -30, 0], y: [0, 40, 0] }}
          transition={{ duration: 18, repeat: Infinity, ease: "easeInOut" }}
        />
        <div className="pointer-events-none absolute inset-0 opacity-[0.06] mix-blend-overlay"
          style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='120' height='120'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='3'/%3E%3C/filter%3E%3Crect width='120' height='120' filter='url(%23n)'/%3E%3C/svg%3E\")" }} />
        <motion.div
          className="pointer-events-none absolute right-[8%] top-24 hidden lg:block"
          initial={{ opacity: 0, x: 60 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 1 }}
        >
          <motion.div animate={{ y: [0, -14, 0] }} transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}>
            <Plane className="h-24 w-24 -rotate-[18deg] text-white/10" />
          </motion.div>
        </motion.div>

        <div className="relative mx-auto max-w-7xl px-4">
          <div className="mx-auto mb-7 max-w-3xl text-center">
            <motion.p
              initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
              className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-1.5 text-[12px] font-bold uppercase tracking-[0.18em] text-[#F5B32B] backdrop-blur"
            >
              <Sparkles className="h-3.5 w-3.5" /> Book International &amp; Domestic
            </motion.p>
            <motion.h1
              variants={stagger} initial="hidden" animate="show"
              className="mt-4 flex flex-wrap justify-center gap-x-3 font-[Poppins] text-4xl font-extrabold leading-tight text-white sm:text-5xl lg:text-6xl"
            >
              {HERO_WORDS.map((w, i) => (
                <motion.span key={i} variants={rise} className={w === "&" ? "text-white/70" : (["Hotels,", "Holiday"].includes(w) ? "bg-gradient-to-r from-[#F5B32B] to-[#E89A10] bg-clip-text text-transparent" : "")}>
                  {w}
                </motion.span>
              ))}
            </motion.h1>
            <motion.p
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.7, duration: 0.6 }}
              className="mx-auto mt-3 max-w-xl text-sm text-white/70 md:text-base"
            >
              One place for real-time flights, handpicked hotels and curated holidays — powered by live fares.
            </motion.p>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 40, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.7, delay: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="rounded-[26px] border border-white/20 bg-white/95 p-2 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.55)] backdrop-blur-xl md:p-3"
          >
            <FlightSearchWidget />
          </motion.div>

          {/* Trust strip */}
          <motion.div
            variants={stagger} initial="hidden" animate="show"
            className="mx-auto mt-7 grid max-w-3xl grid-cols-3 gap-3"
            data-testid="hero-trust"
          >
            {TRUST.map((t) => (
              <motion.div key={t.label} variants={rise}
                className="flex items-center justify-center gap-2.5 rounded-2xl border border-white/10 bg-white/5 px-3 py-3 text-white backdrop-blur transition-colors hover:bg-white/10">
                <t.icon className="h-5 w-5 shrink-0 text-[#F5B32B]" />
                <div className="text-left leading-tight">
                  <p className="text-xs font-bold sm:text-sm">{t.label}</p>
                  <p className="hidden text-[11px] text-white/60 sm:block">{t.sub}</p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      <Reveal testid="reveal-banners"><BannerCarousel /></Reveal>
      <Reveal><PromoAndTicker /></Reveal>
      <Reveal><CouponSection /></Reveal>
      <Reveal><DealsOffers /></Reveal>
      <Reveal><WhyChooseUs /></Reveal>
      <Reveal><PopularDestinations /></Reveal>
      <Reveal><PopularAirlines /></Reveal>
      <Reveal><TopAirlineDeals /></Reveal>
      <Reveal><TopRoutes /></Reveal>
      <Reveal><Blogs /></Reveal>
      <Reveal><Testimonials /></Reveal>
      <Reveal><FaqSection /></Reveal>
      <Reveal><AboutStrip /></Reveal>

      <Footer />
    </div>
  );
};

export default Home;
